// React Hooks
import React, { useContext, useLayoutEffect, useState } from "react";
import { PortalContext } from "App";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
// Custome Component
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import { normalizeAccountStatusDialogData } from "utils/normalizeAccountStatusDialogData";
import StatusWrapper from "components/StatusWrapper/StatusWrapper";
import EmployeeDetail from "./employee-detail/EmployeeDetail";
import EmployeeConfig from "./employee-config/EmployeeConfig";
// MUI Component
import { Stack, Typography, Button, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Box from "@mui/material/Box";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
// API
import {
  queryEmployeeDetail,
  updateEmployee,
  queryEmployeeConfig,
} from "./model";
import { getStatusRemarkOptions } from "../../components/account-status/getStatusRemarkOptions";
// Others
import { spinning } from "../../merchants/offline-merchant-detail/utils";
import { useSnackbar } from "notistack";
import moment from "moment";
import { isNullish } from "utils/utils";
import useColors from "utils/useColors/useColors";
import responseCodeMapping from "utils/response_code_mapping.json";

const OfflineEmployeeDetail = ({ path, redirectFn }) => {
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [employeeId, setEmployeeId] = useState("");
  const [employeeDetail, setEmployeeDetail] = useState({});
  const [employeeConfig, setEmployeeConfig] = useState({});
  const [tabValue, setTabValue] = useState("detail");
  const [isDetailLoading, setIsDetailLoading] = useState(true);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusRemarks, setStatusRemarks] = useState();
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);
  const AGENT_STATUS_INIT_STATE = {
    status: "",
    lockAccountType: "",
    remark: "",
  };
  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    AGENT_STATUS_INIT_STATE,
  );

  // Locale keys
  const EMPLOYEE_LOCALE_KEY = "page.employees.";
  const DIALOG_LOCALE_KEY = "page.dialogs.";

  const c = useColors();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setEmployeeId(detailPageState[path].employeeId);
    } else if (sessionState && sessionState[path]) {
      setEmployeeId(sessionState[path].employeeId);
    } else {
      if (employeeId == "") setEmployeeId("close");
    }
  }, [detailPageState[path]]);
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  useLayoutEffect(() => {
    if (employeeId) {
      initialFetchDetails();
    }
  }, [employeeId]);

  const initialFetchDetails = async () => {
    try {
      setIsDetailLoading(true);
      setIsConfigLoading(true);
      const [statusOptionsResp, employeeDetailResp, employeeConfigResp] = await Promise.all([
        getStatusRemarkOptions(),
        queryEmployeeDetail(employeeId),
        queryEmployeeConfig({ employeeId })
      ]);

      const { data: detailData } = employeeDetailResp;
      if (detailData?.responseCode === "0000" && detailData?.data) {
        setEmployeeDetail(detailData?.data);
        setStatusRemarks(statusOptionsResp?.data?.data);
      }

      const { data: configData } = employeeConfigResp;
      if (configData?.responseCode === "0000" && configData?.data) {
        setEmployeeConfig(configData?.data);
      }
    } catch (error) {
      console.error("Error fetching employee detail and config:", error);
    } finally {
      setIsDetailLoading(false);
      setIsConfigLoading(false);
    }
  };

  const fetchEmployeeDetail = async () => {
    try {
      const employeeDetailResp = await queryEmployeeDetail(employeeId);
      const { data } = employeeDetailResp;
      if (data?.responseCode === "0000" && data?.data) {
        setEmployeeDetail(data?.data);
      }
    } catch (error) {
      console.error("Error fetching employee detail:", error);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const fetchEmployeeConfig = async () => {
    try {
      setIsConfigLoading(true);
      const employeeConfigResp = await queryEmployeeConfig({ employeeId });
      const { data } = employeeConfigResp;
      if (data?.responseCode === "0000" && data?.data) {
        setEmployeeConfig(data?.data);
      }
    } catch (error) {
      console.error("Error fetching employee detail:", error);
    } finally {
      setIsConfigLoading(false);
    }
  };

  const warning = (employee) => {
    const { status, remark, terminatedDate } = employee;

    const alertList = [];

    const ErrorAlert = ({ id, description }) => (
      <Alert
        key={id}
        sx={{
          marginTop: "10px",
          borderRadius: "8px",
          border: "1px solid #f44336",
        }}
        severity="error"
        icon={<CancelOutlinedIcon fontSize="medium" />}
      >
        <Typography>{description}</Typography>
      </Alert>
    );

    // Account status warning
    if (["TERMINATED", "SUSPENDED"].includes(status)) {
      alertList.push(
        <ErrorAlert
          id="status-alert"
          description={`Account is ${status.toLowerCase()}. Reason: ${remark} (${moment(terminatedDate).format("YYYY-MM-DD HH:mm:ss")})`}
        />,
      );
    }
    return <>{alertList}</>;
  };

  // -------- Handle Status dialog -------- //
  const accountStatusDialog = {
    open: (data) => {
      setAccountStatusDialogData(normalizeAccountStatusDialogData(data));
      setIsAccountStatusDialogOpen(true);
    },

    close: () => {
      accountStatusDialog.restore();
      setIsAccountStatusDialogOpen(false);
    },

    updatePayload: (fieldInput) => {
      setAccountStatusDialogData(fieldInput);
    },

    content: () => (
      <AccountStatusDialog
        data={accountStatusDialogData}
        remarksList={statusRemarks}
        updatePayload={accountStatusDialog.updatePayload}
      />
    ),

    restore: () => {
      setAccountStatusDialogData(AGENT_STATUS_INIT_STATE);
    },

    validationCheck: () => {
      const selectedReason =
        accountStatusDialogData.reason ?? accountStatusDialogData.lockAccountType;

      if (
        accountStatusDialogData.status !== "ACTIVE" &&
        accountStatusDialogData.status !== "PENDING"
      ) {
        const hasReason =
          typeof selectedReason === "string" && selectedReason.trim() !== "";
        if (!hasReason) {
          snackbar(
            t(DIALOG_LOCALE_KEY + "changeStatus.msgError") +
            " Reason must not be empty!",
            "error",
          );
        }
        return hasReason;
      }
      return true;
    },

    updateResult: async () => {
      if (accountStatusDialog.validationCheck()) {
        setSubmitting(true);
        const reasonList = statusRemarks[accountStatusDialogData.status];
        const remarkOthersKey = "OTHERS";
        const selectedReason =
          accountStatusDialogData.reason ?? accountStatusDialogData.lockAccountType;

        let remarksend =
          reasonList && (reasonList[selectedReason] ?? "");
        if (selectedReason == remarkOthersKey)
          remarksend = accountStatusDialogData.remark;

        let payload = {
          employeeId: employeeDetail?.employeeId ?? employeeId,
          isStatusChange: accountStatusDialogData.status != employeeDetail.status,
          status: accountStatusDialogData.status,
        };

        if (!isNullish(selectedReason))
          payload = {
            ...payload,
            lockAccountType: selectedReason,
          };
        if (!isNullish(remarksend))
          payload = { ...payload, remark: remarksend };

        const apiResponse = await updateEmployee(payload);
        if (!apiResponse || !apiResponse?.data) {
          snackbar(t("general.message.networkError"), "error");
        } else {
          switch (apiResponse.status) {
            case 401: {
              console.log("You have no authorization to view the page");
              return logout();
            }
            case 403: {
              snackbar(t("general.message.unauthorized"), "error");
              break;
            }
            default: {
              const responseBody = apiResponse.data;
              if (responseBody.responseCode !== "0000") {
                const errorMessage =
                  responseCodeMapping[responseBody.responseCode]?.["en"] ||
                  responseBody.message ||
                  responseCodeMapping.error?.["en"] ||
                  "";
                snackbar(
                  t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
                    errorMessage,
                  "error",
                );
              } else {
                snackbar(
                  t(
                    DIALOG_LOCALE_KEY +
                    "changeStatus.msgSuccess." +
                    accountStatusDialogData.status.toLowerCase(),
                  ),
                  "success",
                );
              }
            }
          }
        }
        setSubmitting(false);
        accountStatusDialog.close();
        await fetchEmployeeDetail();
        await fetchEmployeeConfig();
      }
    },
  };
  // -------- Handle Status dialog -------- //

  const header = () => (
    <Stack>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.header.employeeName`)}:{" "}
        {employeeDetail?.email}
      </Typography>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography>
          {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.header.employeeId`)}:{" "}
          {employeeDetail?.employeeId}
        </Typography>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.header.status`)}
          <StatusWrapper status={employeeDetail?.status ?? "ACTIVE"} />
          <Button
            sx={{ display: employeeDetail?.status === "TERMINATED" ? "none" : "" }}
            onClick={() => {
              accountStatusDialog.open(employeeDetail);
            }}
          >
            <EditIcon sx={{ color: c.brandDark12 }} />
          </Button>
        </Stack>
      </Stack>
      <Stack>{warning(employeeDetail)}</Stack>
    </Stack>
  );

  const tabs = () => {
    const tabStyle = {
      "&.Mui-selected": {
        color: c.brandDark12,
      },
      textTransform: "none",
    };
    return (
      <Stack>
        <TabContext value={tabValue}>
          <Box
            sx={{
              borderBottom: 3,
              borderColor: "divider",
              background: "white",
            }}
          >
            <TabList
              onChange={(event, newValue) => {
                setTabValue(newValue);
              }}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: c.brandDark12,
                },
                "& .MuiTab-root": {
                  whiteSpace: "nowrap",
                },
              }}
              variant="standard"
              scrollButtons="auto"
            >
              <Tab
                label={t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.detail.title`)}
                value="detail"
                sx={tabStyle}
              />
              <Tab
                label={t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.config.title`)}
                value="config"
                sx={tabStyle}
              />
            </TabList>
          </Box>

          {/* Use display attribute to prevent re-fetching the data */}
          <Box display={tabValue === "detail" ? "unset" : "none"}>
            {isDetailLoading ? spinning() : (
              <EmployeeDetail
                employeeDetail={employeeDetail}
                redirectFn={redirectFn}
                fetchEmployeeDetail={fetchEmployeeDetail}
              />
            )}
          </Box>
          <Box display={tabValue === "config" ? "unset" : "none"}>
            {isConfigLoading ? spinning() : (
              <EmployeeConfig
                employeeConfig={employeeConfig}
                fetchEmployeeConfig={fetchEmployeeConfig} />
            )}
          </Box>
        </TabContext>
      </Stack>
    );
  };

  return (
    <Stack
      sx={{
        padding: "24px",
        background: "white",
      }}
      gap={2}
    >
      {header()}
      {tabs()}
      {/* The employee status handler */}
      <SimpleDialog
        open={isAccountStatusDialogOpen}
        dialogOncloseFn={accountStatusDialog.close}
        title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
        content={accountStatusDialog.content()}
        positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
        negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
        positiveActionLoading={submitting}
        negativeActionDisabled={submitting}
        confirmFn={
          !submitting
            ? accountStatusDialog.updateResult
            : () => {
              console.log("submitting?", submitting);
            }
        }
      />
    </Stack>
  );
};

export default React.memo(OfflineEmployeeDetail);
